import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';

import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreForm,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';

import {
  CreditTransaction,
  LimitedPartner,
  Reservation,
  // Subscriber,
  getCreditTransactionTypeSelectItems,
  useCreditTypes,
  useFunds,
} from '../../hooks/useDomainData';
import {useMutation, useQueryClient} from 'react-query';
import {useAuthPersonInfo} from '../..//hooks/usePersonInfo';
import {useAlerts} from 'react-native-paper-alerts';

export interface CreditTransactionEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  allLimitedPartners: ReadonlyArray<LimitedPartner>;
  filteredReservations: ReadonlyArray<Reservation>;
  creditTransaction?: CreditTransaction;
}

export function CreditTransactionEditor(
  props: CreditTransactionEditorProps,
): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();

  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();
  const creditTypes = useCreditTypes();
  const funds = useFunds();
  const authPersonInfo = useAuthPersonInfo();

  const [validating, setValidating] = useState(false);

  const saveCreditTransaction = useMutation(
    (creditTransaction: CreditTransaction) => {
      // remove properties that just are optional && undefined
      if (creditTransaction.reservationId === undefined) {
        delete creditTransaction.reservationId;
      }
      if (creditTransaction.notes === undefined) {
        delete creditTransaction.notes;
      }

      if (
        props.creditTransaction === undefined ||
        props.creditTransaction.ref === undefined
      ) {
        // console.log(
        //   'adding this creditTransaction: ' +
        //     JSON.stringify(creditTransaction, null, 2),
        // );
        return (
          firestore()
            .collection('creditTransactions')
            // FIXME the totalling if you make 3 -30 is not correct? should be -90
            // FIXME we need to have notes field
            // the types of .add and .set are incompatible, but we don't need the return value
            .add(creditTransaction) as Promise<unknown> as Promise<void>
        );
      }

      // console.log(
      //   'saving this creditTransaction: ' +
      //     JSON.stringify(creditTransaction, null, 2),
      // );

      return (
        firestore()
          .collection('creditTransactions')
          // FIXME the totalling if you make 3 -30 is not correct? should be -90
          // FIXME we need to have notes field
          .doc(props.creditTransaction.ref.id)
          .set(creditTransaction, {merge: true})
      );
    },
    {
      onSettled: () => {
        // console.log('saving succeeded');
        return queryClient.invalidateQueries('creditTransactions');
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const creditTransactionTypeSelect =
    getCreditTransactionTypeSelectItems(funds);

  // console.log('CreditTransactionEditor props: ' + JSON.stringify(props));

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  let creditTransaction: {[key: string]: FormikStoreType} = {
    creditTransactionId: Date.now(),
    allocationDate: new Date(),
    credits: undefined,
    limitedPartnerId:
      props.allLimitedPartners.length === 1
        ? Array.from(props.allLimitedPartners)[0].ref
        : undefined,
  };
  if (props.creditTransaction !== undefined) {
    if (props.creditTransaction.allocationDate instanceof Date) {
      creditTransaction.allocationDate = props.creditTransaction.allocationDate;
    } else {
      creditTransaction.allocationDate = (
        props.creditTransaction
          .allocationDate as FirebaseFirestoreTypes.Timestamp
      ).toDate();
    }
    creditTransaction.credits = props.creditTransaction.credits;
    creditTransaction.creditTransactionId =
      props.creditTransaction.creditTransactionId;
    creditTransaction.creditTypeId = props.creditTransaction.creditTypeId;

    if (props.creditTransaction.expirationDate !== undefined) {
      creditTransaction.expirationDate = (
        props.creditTransaction
          .expirationDate as FirebaseFirestoreTypes.Timestamp
      ).toDate();
    }

    creditTransaction.limitedPartnerId =
      props.creditTransaction.limitedPartnerId;
    creditTransaction.reservationId = props.creditTransaction.reservationId;

    creditTransaction.transactionType = props.creditTransaction.transactionType;
    creditTransaction.managedAutomatically =
      props.creditTransaction.managedAutomatically ?? false;
    // The list of current transaction types may not include the one already in the credit transaction
    // If that is the case, add the existing transaction type to our transaction type selector
    if (
      !creditTransactionTypeSelect.includes(creditTransaction.transactionType)
    ) {
      creditTransactionTypeSelect.push(creditTransaction.transactionType);
    }
    creditTransaction.notes = props.creditTransaction.notes;
  }

  return (
    <Portal>
      <Modal
        // style={[styles.container, styles.center]}
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${
                props.creditTransaction ? 'Edit' : 'Create'
              } Credit Transaction`,
              firestoreObject: creditTransaction,
              fieldMetadata: [
                {
                  key: 'creditTransactionId',
                  name: 'Transaction ID',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable: false,
                  errorText: 'Transaction ID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'allocationDate',
                  name: 'Transaction Date',
                  type: FormikStoreTypes.date,
                  required: true,
                  editable:
                    authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
                  errorText: 'Please select a date',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'limitedPartnerId',
                  name: 'Limited Partner',
                  type: FormikStoreTypes.select,
                  required: true,
                  editable:
                    authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
                  errorText: 'Limited Partner is a required field',
                  selectItems: {
                    documents: Array.from(props.allLimitedPartners),
                    items: Array.from(props.allLimitedPartners).map(
                      limitedPartner => limitedPartner.ref,
                    ),
                    labelExtractor: limitedPartner =>
                      limitedPartner?.limitedPartnerName,
                  },
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'reservationId',
                  name: 'Associated Reservation',
                  required: false,
                  editable:
                    authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
                  errorText: 'Please enter a valid reservation id',
                  type: FormikStoreTypes.select,

                  selectItems: {
                    documents: props.filteredReservations,
                    items: props.filteredReservations.map(
                      reservation => reservation.ref,
                    ),
                    labelExtractor: reservation =>
                      reservation?.limitedPartner?.limitedPartnerName +
                      ' | ' +
                      reservation?.property?.propertyName +
                      ' (' +
                      reservation?.reservationStartJSDate?.toDateString() +
                      ' - ' +
                      reservation?.reservationEndJSDate?.toDateString() +
                      ')',
                  },
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'credits',
                  name: 'Credits',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable:
                    authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
                  validator: DefaultFormikStoreValidator,
                  errorText:
                    'A negative or positive number of credits is required.',
                },
                {
                  key: 'creditTypeId',
                  name: 'Credit Type',
                  type: FormikStoreTypes.select,
                  required: true,
                  editable:
                    authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
                  errorText: 'Credit type is a required field',
                  selectItems: {
                    documents: creditTypes ?? [],
                    items: creditTypes?.map(creditType => creditType.ref) ?? [],
                    labelExtractor: creditType =>
                      creditType?.fund?.fund + ' - ' + creditType?.creditType,
                  },
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'transactionType',
                  name: 'Transaction Type',
                  type: FormikStoreTypes.select,
                  required: true,
                  editable:
                    authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
                  errorText: 'Transaction type is a required field',
                  selectItems: {items: creditTransactionTypeSelect},
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'managedAutomatically',
                  name: 'Managed by Annual Allocation System',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Must be true or false',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'notes',
                  name: 'Notes',
                  type: FormikStoreTypes.textarea,
                  required: false,
                  errorText: 'Enter optional credit transaction notes',
                  validator: DefaultFormikStoreValidator,
                },
              ],
            },
          ]}
          buttons={
            authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations
              ? [
                  {
                    text: 'Cancel',
                    validate: false,
                    onPress: () => {
                      props.setDialogVisible(false);
                    },
                  },
                  {
                    text: 'Save',
                    validate: true,
                    onPress: formObjects => {
                      setValidating(true);
                      saveCreditTransaction.mutate(
                        formObjects[0]
                          .firestoreObject as unknown as CreditTransaction,
                      );
                      props.setDialogVisible(false);
                    },
                  },
                ]
              : [
                  {
                    text: 'Close',
                    validate: false,
                    onPress: () => {
                      props.setDialogVisible(false);
                    },
                  },
                ]
          }
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
